// What-if simulator (NBA shock package, batch 2).
//
// The user picks one action type and sees the current → projected win
// probability. The endpoint is a pure calculation: nothing is written, so the
// result is a forecast, never a promise. The score tone is the same helper the
// sidebar gauge uses, so a number reads the same everywhere in the cockpit.
import { useState, type ReactElement } from 'react';
import { Select } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';

import {
  NBA_LABEL_KEY_PREFIX,
  nbaActionTitleText,
  nbaImpactText,
} from '../../../shared/format/nba-labels.js';
import { NBA_ACTION_TYPES, type NbaActionType } from '../../../sales-health/nba-engine.js';
import { useDealWhatIf } from '../hooks/useDealNextStep.js';
import { scoreToneColor } from '../lib/score-tone.js';

export interface DealWhatIfSimulatorProps {
  dealId: string;
}

export function DealWhatIfSimulator({ dealId }: DealWhatIfSimulatorProps): ReactElement {
  const { t, tc } = useTranslation();
  const [type, setType] = useState<NbaActionType | null>(null);
  const projection = useDealWhatIf(dealId, type);
  const typeLabel = t(`${NBA_LABEL_KEY_PREFIX}simulator.type`);

  return (
    <section className="entity-panel deal-what-if" data-testid="deal-what-if-simulator">
      <h2>{t(`${NBA_LABEL_KEY_PREFIX}simulator.title`)}</h2>
      <p className="entity-meta deal-what-if-hint">{t(`${NBA_LABEL_KEY_PREFIX}simulator.hint`)}</p>
      <div className="deal-what-if-control">
        <span className="entity-meta">{typeLabel}</span>
        <Select<NbaActionType>
          allowClear
          data-testid="deal-what-if-type"
          value={type}
          aria-label={typeLabel}
          placeholder={typeLabel}
          onChange={(value) => setType(value ?? null)}
          options={NBA_ACTION_TYPES.map((value) => ({
            value,
            label: nbaActionTitleText({ t, tc }, value),
          }))}
        />
      </div>

      {type === null ? null : projection.isPending ? (
        <span className="entity-meta" data-testid="deal-what-if-loading">
          {t(`${NBA_LABEL_KEY_PREFIX}loading`)}
        </span>
      ) : projection.isError ? (
        <span className="deal-what-if-error" data-testid="deal-what-if-error">
          {t(`${NBA_LABEL_KEY_PREFIX}simulator.error`)}
        </span>
      ) : projection.data ? (
        <div className="deal-what-if-result" data-testid="deal-what-if-result">
          <span className="deal-what-if-score">
            {t(`${NBA_LABEL_KEY_PREFIX}score.current`)}: {projection.data.currentScore}%
          </span>
          <span aria-hidden="true">→</span>
          <span
            className="deal-what-if-score"
            data-testid="deal-what-if-projected"
            style={{ color: scoreToneColor(projection.data.projectedScore) }}
          >
            {t(`${NBA_LABEL_KEY_PREFIX}score.projected`)}: {projection.data.projectedScore}%
          </span>
          <span className="deal-what-if-impact">
            {nbaImpactText({ t, tc }, projection.data.projectedImpact)}
          </span>
        </div>
      ) : null}
    </section>
  );
}
